import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import { CopyButton } from '../../ui/copy.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { ErrorNote } from '../ui/bits.js';
import { ThemeToggle } from '../ui/theme.js';
import { BillingSection } from './billing.js';

/**
 * Settings: the account, its security, how the console looks, and the plan.
 *
 * Everything that belongs to the PERSON rather than to one site. What belongs
 * to a site (its members, its origin, taking it down) is that site's own
 * Settings tab; this screen never lists a tenant.
 *
 * NO PASSWORD, AND THE PANEL SAYS WHY IN ONE LINE. Sign-in is a link to the
 * mailbox, and apps/server/src/auth/store.ts records the reasoning for never
 * storing a password. What hardens an account here is a second factor on top
 * of the link, so a stolen mailbox alone is no longer enough, plus the two
 * session controls: an absolute lifetime the server enforces and a button
 * that ends every other session now.
 *
 * SECRETS ARE ON SCREEN FOR AS SHORT A TIME AS POSSIBLE. The enrolment secret
 * is shown only between "Turn on" and a confirmed code; recovery codes only
 * until "I have saved them". Neither is written anywhere but memory, and a
 * reload loses them on purpose: the secret can be minted again, and the codes
 * can be regenerated with a code from the app.
 *
 * The operator (the reload-secret holder) has no account, so the account and
 * security sections say so instead of rendering controls the server would
 * refuse.
 */

/** `JBSWY3DPEHPK3PXP` → `JBSW Y3DP EHPK 3PXP`: easier to type into a phone. */
function grouped(secret: string): string {
  return secret.replace(/(.{4})(?=.)/g, '$1 ');
}

function CodeForm(props: {
  state: DashboardState;
  actions: DashboardActions;
  id: string;
  label: string;
  submit: string;
  busy: boolean;
  onSubmit: () => void;
  allowRecovery: boolean;
}): VNode {
  const { state, actions } = props;
  return html`
    <form
      class="codeform"
      onSubmit=${(e: Event) => {
        e.preventDefault();
        props.onSubmit();
      }}
    >
      <label class="cardlabel" for=${props.id}>${props.label}</label>
      <div class="codeform__row">
        <input
          id=${props.id}
          class="codeform__input"
          type="text"
          inputmode=${props.allowRecovery ? 'text' : 'numeric'}
          autocomplete="one-time-code"
          spellcheck="false"
          placeholder=${props.allowRecovery ? '123456 or a recovery code' : '123456'}
          value=${state.security.code}
          onInput=${(e: Event) => actions.setSecurityCode((e.target as HTMLInputElement).value)}
        />
        <button type="submit" class="btn" disabled=${props.busy || !state.security.code.trim()}>
          ${props.busy ? 'Checking…' : props.submit}
        </button>
        <button type="button" class="btn btn--ghost" onClick=${() => actions.cancelTwoFactor()}>Cancel</button>
      </div>
    </form>
  `;
}

function RecoveryCodes(props: { state: DashboardState; actions: DashboardActions; codes: string[] }): VNode {
  const { state, actions, codes } = props;
  const text = codes.join('\n');
  return html`
    <div class="callout callout--warn" role="status">
      <p class="setsec__strong">Save these recovery codes now. They will not be shown again.</p>
      <p class="setsec__p">
        Each one signs you in once if your phone is lost. Keep them somewhere that is not the phone:
        a password manager or a printed page.
      </p>
      <ul class="recovery">
        ${codes.map((c) => html`<li class="recovery__code"><code>${c}</code></li>`)}
      </ul>
      <div class="setsec__actions">
        ${CopyButton({
          id: 'recovery-codes',
          text,
          copied: state.copied,
          onCopy: actions.copy,
          label: 'Copy all',
          describe: 'the recovery codes',
        })}
        <button type="button" class="btn" onClick=${() => actions.dismissRecoveryCodes()}>
          I have saved them
        </button>
      </div>
    </div>
  `;
}

function TwoFactor(props: { state: DashboardState; actions: DashboardActions }): VNode {
  const { state, actions } = props;
  const sec = state.security;

  if (sec.error === 'unavailable') {
    return html`<p class="sec__p muted">This host does not offer two-step sign-in yet.</p>`;
  }
  if (sec.recoveryCodes) return RecoveryCodes({ state, actions, codes: sec.recoveryCodes });
  if (!sec.twoFactor) {
    return html`<div>
      ${ErrorNote({ message: sec.error, label: 'Could not read your two-step sign-in setting.' })}
      ${sec.error ? null : html`<p class="sec__p muted">Loading…</p>`}
    </div>`;
  }

  if (sec.enrolment) {
    const e = sec.enrolment;
    return html`
      <ol class="steps setsec__steps">
        <li class="step">
          <b class="step__h">Add it to an authenticator app.</b> On this phone, tap${SP}
          <a href=${e.otpauthUri}>open in authenticator</a>. On another device, choose "enter a
          setup key" in the app and type this key:
          <div class="url setsec__key">
            <span class="url__tag">Key</span>
            <code class="url__value">${grouped(e.secret)}</code>
            ${CopyButton({
              id: 'totp-secret',
              text: e.secret,
              copied: state.copied,
              onCopy: actions.copy,
              describe: 'the setup key',
            })}
          </div>
        </li>
        <li class="step">
          <b class="step__h">Type the six digits it shows.</b> That proves the app and the server
          agree before anything changes.
          ${CodeForm({
            state,
            actions,
            id: 'totp-confirm',
            label: 'Code from the app',
            submit: 'Turn on',
            busy: sec.busy === 'confirm',
            onSubmit: () => actions.confirmTwoFactor(),
            allowRecovery: false,
          })}
        </li>
      </ol>
      ${ErrorNote({ message: sec.error })}
    `;
  }

  const tf = sec.twoFactor;
  if (!tf.enabled) {
    return html`
      <p class="sec__p">
        Off. After you click the link in the email, ClawHarbor will also ask for a six-digit code
        from an app on your phone, so someone who can read your email still cannot sign in as you.
      </p>
      ${ErrorNote({ message: sec.error })}
      <button type="button" class="btn" disabled=${sec.busy === 'setup'} onClick=${() => actions.startTwoFactor()}>
        ${sec.busy === 'setup' ? 'Preparing…' : 'Turn on two-step sign-in'}
      </button>
    `;
  }

  return html`
    <p class="sec__p">
      <span class="verdict verdict--healthy"><i class="verdict__dot" aria-hidden="true"></i>On</span>${SP}
      ${tf.confirmedAt ? html`since <span class="when">${tf.confirmedAt.slice(0, 10)}</span>. ` : null}
      ${tf.recoveryRemaining} recovery ${tf.recoveryRemaining === 1 ? 'code' : 'codes'} left.
    </p>
    ${tf.recoveryRemaining <= 2
      ? html`<p class="callout callout--warn" role="status">
          ${tf.recoveryRemaining === 0 ? 'No recovery codes are left.' : 'Recovery codes are running out.'} If
          you lose your phone without one, only the operator of this host can let you back in.
          Generate a new set below.
        </p>`
      : null}
    ${sec.pending
      ? CodeForm({
          state,
          actions,
          id: 'totp-pending',
          label: sec.pending === 'disable' ? 'A code to turn it off' : 'A code to replace the recovery codes',
          submit: sec.pending === 'disable' ? 'Turn off' : 'Generate new codes',
          busy: sec.busy === 'disable' || sec.busy === 'recovery',
          onSubmit: () => actions.submitSecurityCode(),
          allowRecovery: true,
        })
      : html`<div class="setsec__actions">
          <button type="button" class="btn btn--ghost" onClick=${() => actions.askSecurityCode('recovery')}>
            New recovery codes
          </button>
          <button type="button" class="btn btn--ghost" onClick=${() => actions.askSecurityCode('disable')}>
            Turn off
          </button>
        </div>`}
    ${ErrorNote({ message: sec.error })}
  `;
}

export function SettingsScreen(props: { state: DashboardState; actions: DashboardActions; now: number }): VNode {
  const { state, actions } = props;
  const p = state.principal;

  return html`
    <section class="wrap screen settings">
      <div class="screen__top">
        <h1 class="screen__h" tabindex="-1" data-focus-target>Settings</h1>
      </div>

      <div class="setsec" id="account">
        <h2 class="sec__h">Account</h2>
        ${p?.kind === 'account'
          ? html`
              <p class="sec__p">
                Signed in as <b>${p.email}</b>. You sign in with a link sent to this address; there
                is no password to steal or reuse. A session ends after 30 days without use, and
                after 90 days whatever happens.
              </p>
              <div class="setsec__actions">
                <button
                  type="button"
                  class="btn btn--ghost"
                  disabled=${state.security.busy === 'logout-all'}
                  onClick=${() => actions.signOutElsewhere()}
                >
                  ${state.security.busy === 'logout-all' ? 'Signing out…' : 'Sign out everywhere else'}
                </button>
                <button type="button" class="btn btn--ghost" onClick=${() => actions.signOut()}>Sign out</button>
              </div>
            `
          : html`<p class="sec__p">
              This page is held by the operator's reload secret, which is not an account: there is
              nothing to secure here beyond keeping the secret out of other hands.
            </p>`}
      </div>

      ${p?.kind === 'account'
        ? html`<div class="setsec" id="security">
            <h2 class="sec__h">Two-step sign-in</h2>
            ${TwoFactor({ state, actions })}
          </div>`
        : null}

      <div class="setsec" id="appearance">
        <h2 class="sec__h">Appearance</h2>
        <p class="sec__p">How this console looks on this browser. Follow the system, or pick one.</p>
        ${ThemeToggle({ theme: state.theme, actions })}
      </div>

      ${BillingSection({ state, actions })}
    </section>
  `;
}
